import React from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { theme } from '../../../style/global-style';
import ArrowSVG from '../../../../public/static/arrow_back.svg';
import {
    StyledModalWrapper,
    StyledModalHeaderWrapper,
    StyledModalBodyWrapper,
    StyledModalHeaderRightIcon,
    StyledModalHeaderBackButton,
    StyledModalHeaderHeadline,
} from './modal.style';

interface IProps {
    showModal: boolean;
    onClose?: () => void;
    onOpen?: () => void; // after the animation
    headerText: string;
    children?: React.ReactNode;
    timeout?: number; // in `ms`
    rightIcon?: JSX.Element;
    'data-testid'?: string;
    fullWidthContent?: boolean;
}
const StyledArrow = styled(ArrowSVG)`
    fill: ${(props) => props.theme.shades.grey6};
    width: 22px;
    height: 17px;
`;

export const Modal = (props: IProps) => {
    const { showModal, onClose, headerText, onOpen, timeout, fullWidthContent } = props;
    const [_showModal, _setShowModal] = React.useState(false);
    const [targetNode, setTargetNode] = React.useState<HTMLElement | null>(null);
    const onCloseHandler = () => {
        _setShowModal(false);
        if (onClose) {
            document.body.style.overflow = 'auto';
            onClose();
        }
    };

    React.useEffect(() => {
        _setShowModal(showModal);
    }, [showModal]);

    React.useEffect(() => {
        showModal && onOpen && setTimeout(() => onOpen(), timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    /*Setting the targetNode to modal after component mounting to avoid server side
    inaccessibility of document node. */
    React.useEffect(() => {
        setTargetNode(document.getElementById('modal'));
    }, []);
    // Checking window object at client side since document object is not accessible on ssr
    if (typeof window !== 'undefined') {
        if (_showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }
    return (
        targetNode &&
        ReactDOM.createPortal(
            <StyledModalWrapper
                showModal={_showModal}
                role="dialog"
                aria-hidden={!_showModal}
                data-testid={props['data-testid'] || 'modal'}
                transitionDuration={props.timeout || theme.transition.duration.modal}
                fullWidthContent={fullWidthContent}
            >
                <div data-testid="modal-content">
                    <StyledModalHeaderWrapper fullWidthContent={fullWidthContent}>
                        <StyledModalHeaderBackButton
                            onClick={onCloseHandler}
                            data-testid="modal-close-btn"
                        >
                            <StyledArrow />
                        </StyledModalHeaderBackButton>
                        <StyledModalHeaderHeadline>{headerText}</StyledModalHeaderHeadline>
                        {props.rightIcon ? (
                            <StyledModalHeaderRightIcon>
                                {props.rightIcon}
                            </StyledModalHeaderRightIcon>
                        ) : null}
                    </StyledModalHeaderWrapper>
                    <StyledModalBodyWrapper className="modal-body" data-testid="modal-body">
                        {props.children}
                    </StyledModalBodyWrapper>
                </div>
            </StyledModalWrapper>,
            targetNode,
        )
    );
};

Modal.defaultProps = {
    timeout: 400,
};
